import { forEach, forOwn, } from './../../utils';
import { ShopError } from './../../internals/errors';
import { OrderType, PIVOTS_KEY_EVENTS_TICKETS, PIVOTS_KEY_PRODUCTS_EVENT_DATES, PIVOTS_KEY_PRODUCTS_METADATA, PIVOTS_KEY_PRODUCTS_TICKETS, PIVOTS_KEY_SHOPS_COLLAPSES, PIVOTS_KEY_SHOPS_METADATA, PIVOTS_KEY_SHOPS_PAYMENT_METHODS, PIVOTS_KEY_SHOPS_PRODUCTS, PIVOTS_KEY_SHOPS_TICKETS, PIVOTS_KEY_TICKETS_GROUPS, PIVOTS_KEY_GROUPS_PRODUCTS, } from './types';
// Holds the already visited guids while iterating over pivots
let visitedGuids = {};
/**
 *  Used to set an incremental index if either the preceding or
 *  computed index sequence seems to be broken / incorrect.
 */
function setIncrementalIndexes(items) {
    let index = 0;
    const setIndex = (item) => {
        item.index = index++;
    };
    // Set the index of every item in the collection
    if (Array.isArray(items)) {
        forEach(items, setIndex);
    }
    else {
        forOwn(items, setIndex);
    }
}
/**
 *  Filter the original pivots to remove all pivots
 *  which have the key from key name present in the new pivots.
 *
 *  @param originalPivots
 *  @param newPivots
 *  @param keyName
 */
function filterFromOneSide(originalPivots, newPivots, keyName) {
    if (!newPivots) {
        return originalPivots || [];
    }
    const newPivotsArray = Object.values(newPivots);
    if (!originalPivots) {
        return newPivotsArray;
    }
    const newGroupedPivots = {};
    forEach(newPivotsArray, (pivot) => {
        newGroupedPivots[pivot[keyName]] = true;
    });
    const filteredList = originalPivots.filter((pivot) => {
        return !newGroupedPivots[pivot[keyName]];
    });
    filteredList.push(...newPivotsArray);
    return filteredList;
}
/**
 *  All pivot logic.
 */
export default class Pivot {
    constructor() {
        // Holder of a reference to the original shop data pivots
        this._pivots = {};
    }
    /**
     *  Used to set a reference to the original shop data's pivots,
     *  so the pivot methods can easily access it.
     */
    setPivots(newPivots) {
        if (!newPivots) {
            throw new ShopError('The provided pivots are undefined');
        }
        // Ensure objects are converted to arrays
        // and only tuples with parent guids from the new pivot tuples are replaced.
        this._pivots[PIVOTS_KEY_EVENTS_TICKETS] = filterFromOneSide(this._pivots[PIVOTS_KEY_EVENTS_TICKETS], newPivots[PIVOTS_KEY_EVENTS_TICKETS], 'event_guid');
        this._pivots[PIVOTS_KEY_PRODUCTS_METADATA] = filterFromOneSide(this._pivots[PIVOTS_KEY_PRODUCTS_METADATA], newPivots[PIVOTS_KEY_PRODUCTS_METADATA], 'product_guid');
        this._pivots[PIVOTS_KEY_PRODUCTS_TICKETS] = filterFromOneSide(this._pivots[PIVOTS_KEY_PRODUCTS_TICKETS], newPivots[PIVOTS_KEY_PRODUCTS_TICKETS], 'ticket_guid');
        this._pivots[PIVOTS_KEY_PRODUCTS_EVENT_DATES] = filterFromOneSide(this._pivots[PIVOTS_KEY_PRODUCTS_EVENT_DATES], newPivots[PIVOTS_KEY_PRODUCTS_EVENT_DATES], 'event_date_guid');
        this._pivots[PIVOTS_KEY_SHOPS_COLLAPSES] = filterFromOneSide(this._pivots[PIVOTS_KEY_SHOPS_COLLAPSES], newPivots[PIVOTS_KEY_SHOPS_COLLAPSES], 'collapse_guid');
        this._pivots[PIVOTS_KEY_SHOPS_METADATA] = filterFromOneSide(this._pivots[PIVOTS_KEY_SHOPS_METADATA], newPivots[PIVOTS_KEY_SHOPS_METADATA], 'metadata_guid');
        this._pivots[PIVOTS_KEY_SHOPS_PRODUCTS] = filterFromOneSide(this._pivots[PIVOTS_KEY_SHOPS_PRODUCTS], newPivots[PIVOTS_KEY_SHOPS_PRODUCTS], 'product_guid');
        this._pivots[PIVOTS_KEY_SHOPS_TICKETS] = filterFromOneSide(this._pivots[PIVOTS_KEY_SHOPS_TICKETS], newPivots[PIVOTS_KEY_SHOPS_TICKETS], 'ticket_guid');
        this._pivots[PIVOTS_KEY_SHOPS_PAYMENT_METHODS] = Object.values(newPivots[PIVOTS_KEY_SHOPS_PAYMENT_METHODS] || []);
        this._pivots[PIVOTS_KEY_TICKETS_GROUPS] = filterFromOneSide(this._pivots[PIVOTS_KEY_TICKETS_GROUPS], newPivots[PIVOTS_KEY_TICKETS_GROUPS], 'ticket_guid');
        this._pivots[PIVOTS_KEY_GROUPS_PRODUCTS] = filterFromOneSide(this._pivots[PIVOTS_KEY_GROUPS_PRODUCTS], newPivots[PIVOTS_KEY_GROUPS_PRODUCTS], 'group_id');
        // Check if any pivots in the data are missing from the above code!
        // If it is, add it without any filtering
        forOwn(newPivots, (newPivot, key) => {
            if (!this._pivots[key]) {
                console.error('Pivot key not recognized', key);
                this._pivots[key] = Object.values(newPivot || []);
            }
        });
    }
    /**
     *  Used to get a specific pivot(s) out of the forwarded pivots data, with
     *  the ability to only get pivots that contain a specific guid by
     *  forwarding filter settings.
     */
    get(settings) {
        let pivots = this._pivots[settings.key];
        if (!pivots) {
            throw new ShopError(`No pivots available for: ${settings.key}`);
        }
        // Return all the pivots if no specific item is requested
        if (!settings.item) {
            return pivots;
        }
        pivots = pivots.filter((pivot) => {
            if (!settings.item) {
                return;
            }
            let found = pivot[settings.item.guid_key] === settings.item.guid;
            // Make sue it also belongs to a specific
            // guid when the belongs to filter is set
            if (found && settings.belongs_to) {
                found = pivot[settings.belongs_to.guid_key] === settings.belongs_to.guid;
            }
            return found;
        });
        return pivots;
    }
    /**
     *
     *  Used for determining the index of an item.
     *
     *  Either based on a stern-brocot sequence, or a linked list.
     *
     *  In case of stern-brocot, the fraction is returned as is,
     *  in case of a linked list, the index of an item is
     *  recursively computed based on the pivots,
     *  by iterating over all of them and counting
     *  how much steps it takes to reach the item that does not
     *  have a preceding item.
     *
     *  (i) If the preceding sequence is broken - because the preceding
     *  pivot is undefined or multiple pivots refer to each other - it
     *  will give every item an index based on his returned order,
     *  break the iteration and return -1
     *
     */
    computeIndex(items, settings, index = -1) {
        // An item is needed to find the item in the pivots
        if (!settings.item) {
            setIncrementalIndexes(items);
            return [-1, OrderType.Incremental];
        }
        // Get the pivot of the item
        const pivot = this.get(settings)[0];
        // Add a random index if the requested pivot is not found,
        // as that means the preceding sequence is broken
        if (!pivot) {
            setIncrementalIndexes(items);
            return [-1, OrderType.Incremental];
        }
        if (Number.isInteger(pivot.numerator) && Number.isInteger(pivot.denominator)) {
            return [pivot.numerator / pivot.denominator, OrderType.SternBrocot];
        }
        return this.computeIndexForLinkedList(items, settings, pivot, index);
    }
    /**
     *
     *  Used for recursively computing the index of an item based
     *  on the pivots, by iterating over all of them and counting
     *  how much steps it takes to reach the item that does not
     *  have a preceding item (linked list).
     *
     *  (i) If the preceding sequence is broken - because the preceding
     *  pivot is undefined or multiple pivots refer to each other - it
     *  will give every item an index based on his returned order,
     *  break the iteration and return -1
     *
     */
    computeIndexForLinkedList(items, settings, pivot, index = -1) {
        // An item is needed to find the item in the pivots
        if (!settings.item) {
            setIncrementalIndexes(items);
            return [-1, OrderType.Incremental];
        }
        // Return the current index if there is no preceding item guid,
        // as that means the most highest pivot item has been found
        if (!pivot.preceded_by) {
            return [++index, OrderType.LinkedList];
        }
        // Reset the visited guids string when it's about the first iteration
        if (index === -1) {
            visitedGuids = {};
        }
        // Keep track of the guids that were visited
        visitedGuids[pivot[settings.item.guid_key]] = pivot[settings.item.guid_key];
        // Add a random index if the preceding pivot was already visited
        // before, as that means the preceding sequence is broken and
        // it is about to get in an infinite loop
        if (visitedGuids[pivot.preceded_by]) {
            setIncrementalIndexes(items);
            return [-1, OrderType.Incremental];
        }
        // Set the guid of the preceding item as filter; so we can return
        // the current index if it doesn't have one (reached the end) or
        // keep on iterating over his preceding items if it does
        settings.item.guid = pivot.preceded_by;
        // TODO: Make sure this doesn't break because of max recursion depth
        return this.computeIndex(items, settings, ++index);
    }
    /**
     *
     *  Used to sort the forwarded items based on pivot-based indexes,
     *  making use of the pivot's compute index method.
     *
     *  (!) Also makes sure to give every item an index based on their
     *  returned order when all the items got index zero - which happens
     *  when all their preceding_by's are `null`
     *
     */
    computeIndexes(items, settings) {
        // Don't do anything if the provided items collection is empty
        const numberOfItems = Array.isArray(items) ? items.length : Object.keys(items).length;
        if (!numberOfItems) {
            return OrderType.Incremental;
        }
        // Keep track of the computed indexes to check if the computed index sequence is correct
        const indexes = [];
        // If the preceding by sequence is correct
        let validSequence = true;
        let lastOrderType = OrderType.Incremental;
        // Fn for invoking the computation of an item's index
        const computeIndex = (item) => {
            // Construct the right pivot settings for computing the item's
            // index based on the forwarded settings and the guid of the
            // item for which the index is currently being computed
            const pivotSettings = settings;
            // An item is needed to find the index in the pivots
            if (!pivotSettings.item) {
                return;
            }
            pivotSettings.item.guid = item.guid;
            // Compute the index of the item
            const [index, orderType] = this.computeIndex(items, pivotSettings);
            lastOrderType = orderType;
            // If the method returned -1 it means the pivots preceding sequence
            // is broken and it gave the items an index based on their
            // random returned order - so the iteration can stop
            switch (orderType) {
                case OrderType.SternBrocot:
                    item.index = index;
                    break;
                case OrderType.LinkedList:
                    item.index = index;
                    indexes.push(index);
                    break;
                case OrderType.Incremental:
                default:
                    return validSequence = false;
            }
        };
        // Used to validate the computed index sequence
        const validIndexSequence = () => {
            let valid = true;
            let index = 0;
            // Should now be index 0 to ...
            indexes.sort((indexOne, indexTwo) => {
                return indexOne === indexTwo ? 0 : indexOne - indexTwo;
            });
            // So loop through the indexes to make sure
            // the computed sequence is ok
            forEach(indexes, (itemIndex) => {
                if (itemIndex !== index) {
                    valid = false;
                }
                else {
                    index++;
                }
                return valid;
            });
            return valid;
        };
        // Compute the indexes for each item
        if (Array.isArray(items)) {
            forEach(items, computeIndex);
        }
        else {
            forOwn(items, computeIndex);
        }
        // Make sure to set an incremental index when the preceding sequence seems
        // correct but the computed index sequence is not, which happens when multiple
        // item pivots have a preceding_by of `null` (resulting in index 0)
        // There is no index sequence when all indexes are valid stern-brocot sequences.
        // No sequence is considered valid!
        if (validSequence && !validIndexSequence()) {
            setIncrementalIndexes(items);
            return OrderType.Incremental;
        }
        return lastOrderType;
    }
}
//# sourceMappingURL=pivot.js.map